import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { ArchiveOutlined } from '@mui/icons-material';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { TreeItem2, TreeItem2SlotProps } from '@mui/x-tree-view/TreeItem2';
import Paid from '@img/paid.svg';
import { TicketsCountByIds } from '../../interfaces';
import { TreeItem } from './helpers/getTreeData';
import { numWord } from './helpers/numWord';
import * as Styled from './styled';

const GetIcon = ({ archived, paid }: { archived: boolean; paid: boolean }) => {
  if (archived) return <ArchiveOutlined />;
  if (paid) return <Paid />;
  return null;
};

export const TreeItems = ({
  data,
  indeterminate,
  ticketsCountByIds,
  selectedItems,
  disabled: disabledFromState,
}: {
  data: TreeItem[];
  indeterminate: string[];
  ticketsCountByIds?: TicketsCountByIds;
  selectedItems: string[];
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const ticketAmountText = [
    t('SubstituteWorker.ticketOne'),
    t('SubstituteWorker.ticketTwo'),
    t('SubstituteWorker.ticketFive'),
  ] as [string, string, string];

  const getCheckboxProps = (id: string, disabled: boolean) => {
    if (indeterminate.includes(id))
      return {
        size: 'small',
        icon: (
          <IndeterminateCheckBoxIcon
            color="primary"
            sx={{ opacity: disabled ? 0.26 : 1 }}
          />
        ),
        disabled,
      };

    return { size: 'small', disabled };
  };

  return data.map(({ id, label, children, archived, paid, disabled }) => (
    <TreeItem2
      key={id}
      itemId={id}
      label={
        <Styled.TreeItemWrapper>
          <Typography variant="body2">
            {label} <GetIcon archived={archived} paid={paid} />
          </Typography>

          {ticketsCountByIds?.[id] && selectedItems.includes(id) && (
            <Styled.Chip
              label={`${ticketsCountByIds[id]} ${numWord(
                ticketsCountByIds[id],
                ...ticketAmountText
              )}`}
              size="small"
              color="primary"
            />
          )}

          {ticketsCountByIds?.[id] && !selectedItems.includes(id) && (
            <Styled.Chip
              label={`${ticketsCountByIds[id]} ${numWord(
                ticketsCountByIds[id],
                ...ticketAmountText
              )}`}
              size="small"
              variant="outlined"
            />
          )}
        </Styled.TreeItemWrapper>
      }
      slotProps={
        {
          checkbox: getCheckboxProps(id, disabledFromState ?? disabled),
        } as TreeItem2SlotProps
      }
    >
      {!!children.length && (
        <TreeItems
          data={children}
          indeterminate={indeterminate}
          ticketsCountByIds={ticketsCountByIds}
          selectedItems={selectedItems}
          disabled={disabledFromState}
        />
      )}
    </TreeItem2>
  ));
};
